import { SortAscendingOutlined } from '@ant-design/icons';
import { Button, Flex, Popover, type RadioChangeEvent } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import type { TrackEventFunction } from '@/hooks/useTrackEvents/types';
import type { Sort } from '@/utils/types';

import * as Styled from './styles';
import type { SortMenuOption } from './types';
import {
  buildRadioValue,
  buildSortRadioOptions,
  buildSortValueMap
} from './utils';

export type Props = {
  options: SortMenuOption[];
  sort?: Sort;
  onSort: (sort: Sort | undefined) => void;
  trackEvent?: TrackEventFunction;
};

/**
 * Single "Sort by" control shared by every table: a caller-defined list of
 * sort choices (each its own label + field + direction, e.g. "Most Quantity
 * Assigned"), replacing per-column header sort arrows. Icon-only trigger,
 * matching the column-visibility button.
 */
const SortButton = ({ options, sort, onSort, trackEvent }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const radioOptions = useMemo(() => buildSortRadioOptions(options), [options]);
  const sortValueMap = useMemo(() => buildSortValueMap(options), [options]);

  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    buildRadioValue(sort)
  );

  /**
   * Derives the radio selection from the applied `sort` — but only while the
   * popover is closed.
   *
   * `sort` is a fresh object on any background URL write, so re-deriving while
   * the popover is open discards the pick the user has not applied yet.
   *
   * `isOpen` is a dependency, not just a guard, so closing re-syncs: a sort
   * applied elsewhere is picked up on the way out, when there is no unapplied
   * pick left to lose.
   */
  useEffect(() => {
    if (isOpen) return;

    setSelectedValue(buildRadioValue(sort));
  }, [isOpen, sort]);

  const handleChange = (event: RadioChangeEvent) => {
    setSelectedValue(event.target.value);
  };

  // Same params as the column-header sort, so the event has one shape
  // whichever affordance fired it.
  const handleApply = () => {
    const nextSort = selectedValue
      ? sortValueMap.get(selectedValue)
      : undefined;

    if (nextSort) {
      trackEvent?.('Click to apply sort', {
        column: nextSort.sort_by,
        order: nextSort.sort_order
      });
    } else {
      trackEvent?.('Click to clear sort');
    }

    setIsOpen(false);
    onSort(nextSort);
  };

  const handleClear = () => {
    if (sort) {
      trackEvent?.('Click to clear sort', { column: sort.sort_by });
    } else {
      trackEvent?.('Click to clear sort');
    }

    setSelectedValue(undefined);
    onSort(undefined);
    setIsOpen(false);
  };

  return (
    <Popover
      title='Sort by'
      placement='bottomRight'
      arrow={false}
      trigger='click'
      open={isOpen}
      onOpenChange={setIsOpen}
      content={
        <Styled.Container>
          <Styled.RadioGroup
            options={radioOptions}
            value={selectedValue}
            onChange={handleChange}
          />
          <Flex gap={8}>
            <Button block onClick={handleClear}>
              Clear
            </Button>
            <Button block type='primary' onClick={handleApply}>
              Apply
            </Button>
          </Flex>
        </Styled.Container>
      }>
      <Button
        aria-label='Sort by'
        data-testid='sort-button'
        color='default'
        variant={sort ? 'solid' : 'outlined'}
        icon={<SortAscendingOutlined />}
      />
    </Popover>
  );
};

export default SortButton;
